// R115 A1 — the MTR view on the Calendar (REQUESTS A1: tenants with payment
// status + open WOs, vacant / try-MTR homes, the recommended MTR rate, listed
// rates per platform, + Lead, tours ON the calendar, leads/tours moved along
// the pipeline). The view itself is DATA in ../presets.ts ('mtr'); this module
// only registers the keys the contract's ownership table gives the MTR
// builder, plus the chip kinds its chips open through:
//   layer    mtr  (load → GET /api/mtr/calendar; data carries `homes` for the MTR rowFilter)
//   stats    mtr_vacant, try_mtr_windows, rent_owed, tours_week
//   rails    try_mtr, leads_by_move_in, tours_week
//   inspectors lead, tenant  (+ their sections and actions)
//   sections listing.mtr_rates, stay.mtr_tenant
//   actions  date.tour, date.lead
//   chipKinds mtr_vacancy, tour, lead, tenant
// Writes: the MTR module's own routes only (/api/mtr/leads…, /tours, /channels,
// /tenants/:id/payment). Money: rent $ = office parity with MTR → Tenants
// (DECISIONS R115); the whole layer is office-only (server-gated too).
import {
  registerLayer, registerStat, registerRail, registerInspector, registerSection, registerAction, registerChipKind, registerBusy, registerCss,
  type OfficeRole, type ViewId, type EntityRef, type CalendarCtx, type DateCell,
} from '../registry';
import {
  LAYER, STAGES_FALLBACK, TERMINAL, SETTLED, ov, money, stageLabel, chipsFor, badgesFor, decorateStay, dedicatedVacant, tryHomes, owingHomes, weekTours,
  railTryMtr, railLeads, railTours, tenantsForStay, timeLabel, type MtrOverlay, type MtrLead, type MtrTenant,
} from './model';
import {
  st, busy, resetState, api, safeLead, leadOf, goMtr, ratesHtml, bindRates, tenantHtml, bindTenant, tenantsForRef, openLedger,
  leadPipelineHtml, bindLeadPipeline, leadToursHtml, bindTours, leadDetailsHtml, bindLeadDetails, openVacancyCard, openTourCard, moveTourTo,
  openLeadForm, openTourForm, openNoteForm, openReasonForm, moveLead,
} from './ui';
import { MTR_CSS } from './css';

const OFFICE: OfficeRole[] = ['admin', 'director', 'manager', 'remote_ops'];
const ALL_VIEWS: ViewId[] = ['stays', 'ops', 'guests', 'mtr', 'health'];

// per-overlay memo (stat match predicates run once per row / stay / chip)
const memo = new WeakMap<object, Map<string, unknown>>();
function m<T>(o: MtrOverlay, key: string, fn: () => T): T {
  let mm = memo.get(o as object);
  if (!mm) { mm = new Map(); memo.set(o as object, mm); }
  if (!mm.has(key)) mm.set(key, fn());
  return mm.get(key) as T;
}
// ov() returns a normalised COPY — memo on the raw layer object instead
const raw = (ctx: Pick<CalendarCtx, 'layer'>) => ctx.layer<MtrOverlay>(LAYER);
function sets(ctx: Pick<CalendarCtx, 'layer' | 'asOf' | 'fmt'>) {
  const r = raw(ctx), o = ov(ctx);
  if (!r || !o) return null;
  return m(r, 'sets', () => ({
    ded: dedicatedVacant(o), tryH: tryHomes(o), owing: owingHomes(o),
    week: new Set(weekTours(o, ctx.asOf, ctx.fmt.addDays).map(t => t.id)),
    weekHomes: new Set(weekTours(o, ctx.asOf, ctx.fmt.addDays).map(t => t.listing_id).filter(Boolean) as string[]),
  }));
}

// ── the layer ────────────────────────────────────────────────────────────
registerLayer({
  id: LAYER, label: 'MTR', swatch: 'var(--accent)', roles: OFFICE, scope: 'day',
  load: async (q, signal) => {
    const r = await fetch(`/api/mtr/calendar?from=${encodeURIComponent(q.from)}&to=${encodeURIComponent(q.to)}`, { signal });
    const j = await r.json().catch(() => null);
    if (!r.ok || !j || !Array.isArray(j.homes)) throw new Error((j && j.error) || (r.status === 404 ? 'this server has no MTR overlay yet' : `MTR overlay unavailable (${r.status})`));
    for (const l of (Array.isArray(j.leads) ? j.leads : [])) if (l && l.id) st.leadCache.set(String(l.id), l);
    return j;
  },
  chips: (p, ctx) => chipsFor(p, ctx),
  badges: (p, ctx) => badgesFor(p, ctx),
  decorateStay: (s, ctx) => decorateStay(s, ctx),
  count: (ctx) => {
    const o = ov(ctx);
    if (!o) return null;
    return o.vacancies.filter(v => v.try_mtr && v.bar).length + o.tenants.filter(t => t.status === 'active').length + o.tours.length;
  },
});

// ── stats (KPI cards = the one active filter) ─────────────────────────────
// R115 review: GET /api/mtr/calendar degrades piece by piece (errors.tenants / leads / vacancy…) — a stat whose piece
// failed reads '—' with a warning (never "Rent owed $0" / "every MTR-only home is let") and doesn't filter rows.
const failed = (ctx: Pick<CalendarCtx, 'layer'>, ...keys: string[]): string | null => { const o = ov(ctx); const er = o && o.errors; if (!er) return null; for (const k of keys) if (er[k]) return k; return null; };
const unread = (k: string) => ({ value: '—', tone: 'warn' as const, sub: `couldn't load the ${k === 'vacancy' ? 'vacancy list' : k} — ↻ to retry` });
registerStat({
  key: 'mtr_vacant', label: 'MTR-only vacant', icon: '🏠', layer: LAYER,
  compute: (ctx) => {
    const o = ov(ctx);
    if (!o) return null;
    const f = failed(ctx, 'vacancy'); if (f) return unread(f);
    const n = o.summary.mtr_vacant;
    return { value: n, tone: n ? 'bad' : 'good', sub: n ? 'no STR fallback — fill these first' : 'every MTR-only home is let' };
  },
  match: {
    rows: (p, ctx) => !!failed(ctx, 'vacancy') || !!sets(ctx)?.ded.has(p.listing_id),
    chips: (c, ctx) => c.entity.kind === 'mtr_vacancy' && !!sets(ctx)?.ded.has(c.listing_id),
  },
});
registerStat({
  key: 'try_mtr_windows', label: 'Try-MTR windows', icon: '🎯', layer: LAYER,
  compute: (ctx) => {
    const o = ov(ctx);
    if (!o) return null;
    const f = failed(ctx, 'vacancy'); if (f) return unread(f);
    const n = o.summary.try_mtr_windows, d = o.summary.try_mtr_dedicated;
    return { value: n, tone: n ? 'warn' : 'good', sub: n ? `${d} MTR-only · ${n - d} seasonal · 28+ open nights` : 'no 28+ night gaps in the next 60 days' };
  },
  match: {
    rows: (p, ctx) => !!failed(ctx, 'vacancy') || !!sets(ctx)?.tryH.has(p.listing_id),
    chips: (c) => c.entity.kind === 'mtr_vacancy',
  },
});
registerStat({
  key: 'rent_owed', label: 'Rent owed', icon: '💲', layer: LAYER,
  compute: (ctx) => {
    const o = ov(ctx);
    if (!o) return null;
    const f = failed(ctx, 'tenants'); if (f) return unread(f);
    const s = o.summary;
    // R116: a check that is clearing is not owed — say it apart
    return { value: money(s.rent_owed), tone: s.rent_owed > 0 ? 'bad' : 'good', sub: `${s.owing_tenants} tenant${s.owing_tenants === 1 ? '' : 's'} behind${s.rent_clearing ? ` · ${money(s.rent_clearing)} clearing` : ''}${s.untracked ? ` · ${s.untracked} without rent set` : ''}` };
  },
  match: {
    rows: (p, ctx) => !!failed(ctx, 'tenants') || !!sets(ctx)?.owing.has(p.listing_id),
    stays: (s, ctx) => { const o = ov(ctx); return !!o && tenantsForStay(o, s).some(t => t.payment.owed_amount > 0); },
    chips: (c, ctx) => c.entity.kind === 'tenant' && !!ov(ctx)?.tenants.some(t => t.tenant_id === c.entity.id && t.payment.owed_amount > 0),
  },
});
registerStat({
  key: 'tours_week', label: 'Tours this week', icon: '🗓', layer: LAYER,
  compute: (ctx) => {
    const o = ov(ctx);
    if (!o) return null;
    const f = failed(ctx, 'leads'); if (f) return unread(f);
    const w = weekTours(o, ctx.asOf, ctx.fmt.addDays);
    const nx = w[0];
    return { value: w.length, sub: nx ? `next ${ctx.fmt.wmd(nx.date)} ${timeLabel(nx.at)} · ${nx.lead_name}` : `${o.summary.leads_open} open lead${o.summary.leads_open === 1 ? '' : 's'}` };
  },
  match: {
    rows: (p, ctx) => !!failed(ctx, 'leads') || !!sets(ctx)?.weekHomes.has(p.listing_id),
    chips: (c, ctx) => c.entity.kind === 'tour' && !!sets(ctx)?.week.has(c.entity.id),
  },
});

// ── rails ("📥 To do") ─────────────────────────────────────────────────────
registerRail({ key: 'try_mtr', label: 'Try MTR', layer: LAYER, items: (ctx) => railTryMtr(ctx) });
registerRail({ key: 'leads_by_move_in', label: 'Leads by move-in', layer: LAYER, items: (ctx) => railLeads(ctx) });
registerRail({ key: 'tours_week', label: 'Tours this week', layer: LAYER, items: (ctx) => railTours(ctx) });

// ── inspectors: lead, tenant ──────────────────────────────────────────────
const homeName = (ctx: CalendarCtx, id: string | null | undefined) => (id ? (ctx.property(id)?.name || `home ${id}`) : 'no home yet');
registerInspector({
  kind: 'lead',
  title: (_ref, data) => (data as MtrLead | null)?.name || 'MTR lead',
  sub: (_ref, data, ctx) => {
    const l = data as MtrLead | null;
    if (!l) return '';
    return `${stageLabel(l.stage)} · ${homeName(ctx, l.property_interest)}${l.move_in ? ` · move-in ${ctx.fmt.md(l.move_in)}` : ''}`;
  },
  load: async (ref, _signal, ctx) => {
    const hit = leadOf(ctx, ref.id);
    if (hit) return hit;
    // not in the window's overlay (e.g. Lost) — the board answers it; keep only the safe shape
    const r = await api('GET', '/api/mtr/leads');
    const v = r.ok && Array.isArray(r.j.leads) ? safeLead(r.j.leads.find((x: any) => x && x.id === ref.id)) : null;
    if (!v) throw new Error(r.ok ? 'This lead is no longer on the board.' : (r.j.error || 'Could not load the lead.'));
    st.leadCache.set(v.id, v);
    return v;
  },
  busy, reset: resetState,
});
registerInspector({
  kind: 'tenant',
  title: (_ref, data) => (data as MtrTenant | null)?.name || 'MTR tenant',
  sub: (_ref, data, ctx) => {
    const t = data as MtrTenant | null;
    if (!t) return '';
    return `${homeName(ctx, t.listing_id)} · lease ${t.lease_start ? ctx.fmt.md(t.lease_start) : '?'} → ${t.lease_end ? ctx.fmt.md(t.lease_end) : 'open-ended'}`;
  },
  load: async (ref, _signal, ctx) => {
    const t = ov(ctx)?.tenants.find(x => x.tenant_id === ref.id);
    if (!t) throw new Error('This tenant is not on the calendar window — open MTR → Tenants.');
    return t;
  },
  busy, reset: resetState,
});

// ── sections ──────────────────────────────────────────────────────────────
registerSection({
  inspector: 'listing', key: 'mtr_rates', title: 'MTR rate & listings', order: 40, openIn: ['mtr'],
  when: (ref, _d, ctx) => { const o = ov(ctx); return !!o && (o.homes.includes(ref.id) || !!o.rates[ref.id]); },
  render: (ref, _d, ctx) => ratesHtml(ctx, ref.id),
  bind: (root, ref, _d, ctx) => {
    const redraw = () => { root.innerHTML = ratesHtml(ctx, ref.id); bindRates(root, ref.id, ctx, redraw); };
    bindRates(root, ref.id, ctx, redraw);
  },
  busy,
});
registerSection({
  inspector: 'stay', key: 'mtr_tenant', title: 'MTR tenant', order: 15, openIn: ['mtr'],
  when: (ref, data, ctx) => tenantsForRef(ctx, ref, data).length > 0,
  render: (ref, data, ctx) => tenantHtml(ctx, tenantsForRef(ctx, ref, data)),
  bind: (root, _ref, _d, ctx) => bindTenant(root, ctx),
});
registerSection({
  inspector: 'tenant', key: 'mtr_tenant', title: 'Rent & work orders', order: 10, openIn: ALL_VIEWS,
  when: () => true,
  render: (ref, data, ctx) => tenantHtml(ctx, tenantsForRef(ctx, ref, data)),
  bind: (root, _ref, _d, ctx) => bindTenant(root, ctx),
});
registerSection({
  inspector: 'lead', key: 'lead_pipeline', title: 'Pipeline', order: 10, openIn: ALL_VIEWS,
  when: (_r, data) => !!data,
  render: (_r, data, ctx) => leadPipelineHtml(ctx, data as MtrLead),
  bind: (root, _r, data, ctx) => bindLeadPipeline(root, ctx, data as MtrLead),
});
registerSection({
  inspector: 'lead', key: 'lead_tours', title: 'Tours', order: 20, openIn: ALL_VIEWS,
  when: (_r, data) => !!data,
  render: (_r, data, ctx) => leadToursHtml(ctx, data as MtrLead),
  bind: (root, _r, data, ctx) => bindTours(root, ctx, data as MtrLead),
});
registerSection({
  inspector: 'lead', key: 'lead_details', title: 'Details', order: 30, openIn: ALL_VIEWS,
  when: (_r, data) => !!data,
  render: (_r, data, ctx) => leadDetailsHtml(ctx, data as MtrLead),
  bind: (root, _r, _d, ctx) => bindLeadDetails(root, ctx),
});

// ── actions ───────────────────────────────────────────────────────────────
const asLead = (d: unknown) => d as MtrLead | null;
registerAction({ key: 'tour', inspector: 'lead', label: 'Schedule tour', icon: '🗓', order: 10, roles: OFFICE, primaryIn: ALL_VIEWS,
  when: (_r, d) => !!asLead(d) && !TERMINAL.includes(asLead(d)!.stage),
  run: (_r, d, ctx) => { const l = asLead(d)!; openTourForm(ctx, { listing_id: l.property_interest, date: ctx.asOf, lead_id: l.id }); } });
registerAction({ key: 'note', inspector: 'lead', label: 'Add note', icon: '📝', order: 20, roles: OFFICE,
  when: (_r, d) => !!asLead(d), run: (_r, d, ctx) => openNoteForm(ctx, asLead(d)!) });
registerAction({ key: 'pipeline', inspector: 'lead', label: 'Open in Pipeline', icon: '↗', order: 30, roles: OFFICE,
  when: (_r, d) => !!asLead(d), run: (_r, d, ctx) => goMtr(ctx, 'leads', asLead(d)!.id) });
// R115 review: the ⋯ menu offers the NEXT step and Mark lost / ended (with the reason form) — not ten "Move to …"
// entries that skipped the Pipeline section's back-move / reopen confirm. Any other move: the Stage select.
const nextStage = (l: MtrLead): string | null => { const i = STAGES_FALLBACK.indexOf(l.stage); const n = i >= 0 ? STAGES_FALLBACK[i + 1] : null; return n && !TERMINAL.includes(n) ? n : null; };
registerAction({ key: 'stage_next', inspector: 'lead', label: 'Next stage', icon: '→', order: 100, roles: OFFICE,
  when: (_r, d) => { const l = asLead(d); return !!l && !TERMINAL.includes(l.stage) && !!nextStage(l); },
  run: (_r, d, ctx) => { const l = asLead(d)!; const n = nextStage(l); if (n) void moveLead(ctx, l, n, null); } });
registerAction({ key: 'stage_lost', inspector: 'lead', label: 'Mark lost…', icon: '✕', order: 110, roles: OFFICE,
  when: (_r, d) => { const l = asLead(d); return !!l && !TERMINAL.includes(l.stage) && !SETTLED.includes(l.stage); },
  run: (_r, d, ctx) => openReasonForm(ctx, asLead(d)!, 'lost') });
registerAction({ key: 'stage_ended', inspector: 'lead', label: 'Mark ended…', icon: '✕', order: 111, roles: OFFICE,
  when: (_r, d) => { const l = asLead(d); return !!l && SETTLED.includes(l.stage); },
  run: (_r, d, ctx) => openReasonForm(ctx, asLead(d)!, 'ended') });

const asTenant = (d: unknown) => d as MtrTenant | null;
registerAction({ key: 'ledger', inspector: 'tenant', label: 'Rent & notes', icon: '💲', order: 10, roles: OFFICE, primaryIn: ALL_VIEWS,
  when: (_r, d) => !!asTenant(d) && !asTenant(d)!.unsaved, run: (_r, d, ctx) => openLedger(ctx, asTenant(d)!.tenant_id, asTenant(d)!.name) });
// R116 #4 (Kai): a note — "check by 15–17 for checks to clear" — straight from the Calendar (the same panel, composer focused)
registerAction({ key: 'note', inspector: 'tenant', label: 'Add note', icon: '📝', order: 15, roles: OFFICE,
  when: (_r, d) => !!asTenant(d) && !asTenant(d)!.unsaved, run: (_r, d, ctx) => openLedger(ctx, asTenant(d)!.tenant_id, asTenant(d)!.name, 'note') });
registerAction({ key: 'stay', inspector: 'tenant', label: 'Open the reservation', icon: '🛏', order: 20, roles: OFFICE,
  when: (_r, d, ctx) => { const t = asTenant(d); return !!t && !!t.stay_id && ctx.data.stays.some(s => String(s.id) === t.stay_id); },
  run: (_r, d, ctx) => ctx.openInspector({ kind: 'stay', id: asTenant(d)!.stay_id!, listing_id: asTenant(d)!.listing_id }) });
registerAction({ key: 'open_mtr', inspector: 'tenant', label: 'Open in MTR → Tenants', icon: '↗', order: 30, roles: OFFICE,
  when: () => true, run: (_r, _d, ctx) => goMtr(ctx, 'tenants') });

// date actions — the create chooser (ordered by the view's createOrder: MTR puts tour, lead first)
// R115 integ fix (D2): offered with no home too (the toolbar "+ Add"): both forms carry their own home
// picker ("Which home?"; a lead or a tour may stay "not decided").
const cell = (d: unknown) => d as DateCell | null;
registerAction({ key: 'tour', inspector: 'date', label: 'Schedule MTR tour', icon: '🗓', order: 60, roles: OFFICE, primaryIn: ['mtr'],
  when: (ref, d) => !!ref.date && !(cell(d) && cell(d)!.past),
  run: (ref, _d, ctx) => openTourForm(ctx, { listing_id: ref.listing_id || null, date: ref.date! }) });
registerAction({ key: 'lead', inspector: 'date', label: 'Add MTR lead', icon: '👤', order: 61, roles: OFFICE,
  when: (ref, d) => !(cell(d) && cell(d)!.past),
  run: (ref, _d, ctx) => openLeadForm(ctx, { listing_id: ref.listing_id || null, move_in: ref.date || null }) });

// ── chip kinds ────────────────────────────────────────────────────────────
registerChipKind({ kind: 'mtr_vacancy', legend: 'Try-MTR window', open: (ref, anchor, ctx) => openVacancyCard(ref, anchor, ctx) });
registerChipKind({
  kind: 'tour', legend: 'MTR tour',
  open: (ref, anchor, ctx) => openTourCard(ref, anchor, ctx),
  move: {
    canDrop: (ref: EntityRef, date: string, ctx: CalendarCtx) => date >= ctx.asOf && !!ov(ctx)?.tours.some(t => t.id === ref.id && t.status === 'scheduled'),
    onDrop: (ref: EntityRef, date: string, ctx: CalendarCtx) => moveTourTo(ctx, ref.id, date),
  },
});
registerChipKind({ kind: 'lead', legend: 'MTR lead (move-in)', open: (ref, _a, ctx) => ctx.openInspector(ref) });
registerChipKind({ kind: 'tenant', legend: 'MTR tenant (direct lease)', open: (ref, _a, ctx) => ctx.openInspector(ref) });

registerBusy('mtr', busy);
registerCss('mtr', MTR_CSS);
